import {
  Bell,
  Building2,
  CreditCard,
  Key,
  SlidersHorizontal,
  ShieldCheck,
  User,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { type Permission } from 'modules/auth/permissions';

/** The dialog's URL contract. Org slugs mirror each old route's Gate permission (UX-only). */
export type SettingsSection =
  | 'profile'
  | 'org'
  | 'org-preferences'
  | 'org-notifications'
  | 'members'
  | 'roles'
  | 'access-keys'
  | 'billing';

export interface SettingsSectionEntry {
  slug: SettingsSection;
  labelKey: string;
  icon: LucideIcon;
  permission?: Permission;
}

export interface SettingsSectionGroup {
  /** Uppercase eyebrow above the group in the nav. */
  labelKey: string;
  items: SettingsSectionEntry[];
}

export const SECTION_GROUPS: SettingsSectionGroup[] = [
  {
    labelKey: 'account',
    items: [{ slug: 'profile', labelKey: 'settings.nav_profile', icon: User }],
  },
  {
    labelKey: 'settings.group_organization',
    items: [
      { slug: 'org', labelKey: 'organization', icon: Building2, permission: 'ROLES_EDIT' },
      { slug: 'org-preferences', labelKey: 'settings.nav_preferences', icon: SlidersHorizontal, permission: 'ROLES_EDIT' },
      { slug: 'org-notifications', labelKey: 'notifications', icon: Bell, permission: 'ROLES_EDIT' },
    ],
  },
  {
    labelKey: 'settings.group_access',
    items: [
      { slug: 'members', labelKey: 'settings.nav_members', icon: Users, permission: 'MEMBERS_EDIT' },
      { slug: 'roles', labelKey: 'settings.nav_roles', icon: ShieldCheck, permission: 'ROLES_EDIT' },
      { slug: 'access-keys', labelKey: 'settings.nav_access_keys', icon: Key, permission: 'ACCESS_KEYS_CREATE' },
    ],
  },
  {
    labelKey: 'settings.group_billing',
    items: [
      { slug: 'billing', labelKey: 'settings.nav_plan_usage', icon: CreditCard, permission: 'BILLING_EDIT' },
    ],
  },
];

const ALL_ENTRIES = SECTION_GROUPS.flatMap((g) => g.items);

export function canAccessSection(entry: SettingsSectionEntry, permissions: Permission[]): boolean {
  return !entry.permission || permissions.includes(entry.permission);
}

/** Groups → only the entries this user may see; empty groups drop out. */
export function visibleGroups(permissions: Permission[]): SettingsSectionGroup[] {
  return SECTION_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((item) => canAccessSection(item, permissions)),
  })).filter((g) => g.items.length > 0);
}

/** Unknown slugs, and sections whose permission the user lacks, fall back to `profile`. */
export function resolveSection(slug: string | null, permissions: Permission[]): SettingsSection {
  const entry = ALL_ENTRIES.find((e) => e.slug === slug);
  if (entry && canAccessSection(entry, permissions)) return entry.slug;
  return 'profile';
}
